import React, { useState } from 'react';
import { Bold, Italic, Underline, List, LinkIcon, Image } from 'lucide-react';

interface CreatePageFormProps {
  onCancel: () => void;
  mode?: 'create' | 'edit';
  pageId?: string | null;
  initialData?: any;
  onSubmit?: (pageId: string | null, data: any) => void;
}

const CreatePageForm: React.FC<CreatePageFormProps> = ({
  onCancel,
  mode = 'create',
  pageId = null,
  initialData,
  onSubmit
}) => {
  const [pageTitle, setPageTitle] = useState(initialData?.title || '');
  const [urlSlug, setUrlSlug] = useState(initialData?.slug || '/virewire.app/slug/new-landing-page');
  const [pageContent, setPageContent] = useState(initialData?.content || '');
  const [status, setStatus] = useState(initialData?.status || 'Draft');
  const [scheduleDate, setScheduleDate] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState(initialData?.meta_description || '');
  const [category, setCategory] = useState('Marketing');
  const [template, setTemplate] = useState('Standard');
  const [visibility, setVisibility] = useState('Public');

  const handleSubmit = async () => {
    if (!pageTitle.trim()) {
      alert('Please enter a page title');
      return;
    }
    if (!urlSlug.trim()) {
      alert('Please enter a URL slug');
      return;
    }
    if (onSubmit) {
      onSubmit(pageId, {
        title: pageTitle,
        slug: urlSlug,
        content: pageContent,
        meta_description: metaDescription,
        status: status
      });
    } else {
      alert('Submit handler not provided');
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {mode === 'edit' ? 'Edit Page' : 'Create New Page'}
          </h1>
          <p className="text-gray-600">
            {mode === 'edit'
              ? 'Update the page content and settings.'
              : 'Construct a high-performance landing page for the Sovereign ecosystem.'
            }
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - Form Fields */}
          <div className="col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg p-6">
              <h2 className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-4 pb-3 border-b border-gray-200">
                Basic Information
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs uppercase font-bold text-gray-600 tracking-wider mb-2">
                    Page Title
                  </label>
                  <input
                    type="text"
                    placeholder="Enter page title..."
                    value={pageTitle}
                    onChange={(e) => setPageTitle(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase font-bold text-gray-600 tracking-wider mb-2">
                    URL Slug
                  </label>
                  <input
                    type="text"
                    placeholder="/virewire.app/slug/"
                    value={urlSlug}
                    onChange={(e) => setUrlSlug(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
            </div>

            {/* Page Content */}
            <div className="bg-white rounded-lg p-6">
              <h2 className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-4 pb-3 border-b border-gray-200">
                Page Content
              </h2>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <div className="bg-gray-100 p-2 border-b border-gray-300 flex gap-2">
                  <button className="p-1 hover:bg-gray-200 rounded"><Bold className="w-4 h-4" /></button>
                  <button className="p-1 hover:bg-gray-200 rounded"><Italic className="w-4 h-4" /></button>
                  <button className="p-1 hover:bg-gray-200 rounded"><Underline className="w-4 h-4" /></button>
                  <button className="p-1 hover:bg-gray-200 rounded"><LinkIcon className="w-4 h-4" /></button>
                  <button className="p-1 hover:bg-gray-200 rounded"><List className="w-4 h-4" /></button>
                  <button className="p-1 hover:bg-gray-200 rounded"><Image className="w-4 h-4" /></button>
                </div>
                <textarea
                  placeholder="Start writing your content here..."
                  value={pageContent}
                  onChange={(e) => setPageContent(e.target.value)}
                  rows={12}
                  className="w-full px-4 py-3 focus:outline-none resize-none"
                />
              </div>
            </div>

            {/* SEO & Metadata */}
            <div className="bg-white rounded-lg p-6">
              <h2 className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-4 pb-3 border-b border-gray-200">
                SEO & Metadata
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs uppercase font-bold text-gray-600 tracking-wider mb-2">
                    Meta Title <span className="text-gray-400 font-normal">46 / 60 characters</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Title tag for search engines..."
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase font-bold text-gray-600 tracking-wider mb-2">
                    Meta Description <span className="text-gray-400 font-normal">100 / 160 characters</span>
                  </label>
                  <textarea
                    placeholder="Write a compelling summary for Google results..."
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Publishing Settings */}
            <div className="bg-white rounded-lg p-6">
              <h2 className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-4 pb-3 border-b border-gray-200">
                Publishing Settings
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs uppercase font-bold text-gray-600 tracking-wider mb-3">
                    Status
                  </label>
                  <div className="space-y-2">
                    {['Draft', 'Scheduled', 'Published'].map((s) => (
                      <label key={s} className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="status"
                          checked={status === s}
                          onChange={() => setStatus(s)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-gray-700">{s}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase font-bold text-gray-600 tracking-wider mb-2">
                    Schedule Date
                  </label>
                  <input
                    type="text"
                    placeholder="Select date..."
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase font-bold text-gray-600 tracking-wider mb-2">
                    Author
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center text-xs text-white font-semibold">
                      A
                    </div>
                    <span className="text-sm text-gray-700">Alex Hamilton</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Visibility & Access */}
            <div className="bg-white rounded-lg p-6">
              <h2 className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-4 pb-3 border-b border-gray-200">
                Visibility & Access
              </h2>
              <div className="space-y-3">
                {[
                  { value: 'Public', label: 'Public', desc: 'AVAILABLE TO ANYONE' },
                  { value: 'Internal', label: 'Internal', desc: 'PORTAL USERS ONLY' },
                  { value: 'Hidden', label: 'Hidden', desc: 'DIRECT LINK ONLY' }
                ].map((option) => (
                  <label key={option.value} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="visibility"
                      checked={visibility === option.value}
                      onChange={() => setVisibility(option.value)}
                      className="w-4 h-4 mt-1"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{option.label}</p>
                      <p className="text-xs text-gray-600">{option.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Page Attributes */}
            <div className="bg-white rounded-lg p-6">
              <h2 className="text-xs uppercase font-bold text-gray-600 tracking-wider mb-4 pb-3 border-b border-gray-200">
                Page Attributes
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs uppercase font-bold text-gray-600 tracking-wider mb-2">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option>Marketing</option>
                    <option>Legal</option>
                    <option>Support</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase font-bold text-gray-600 tracking-wider mb-2">
                    Template
                  </label>
                  <select
                    value={template}
                    onChange={(e) => setTemplate(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option>Standard</option>
                    <option>Landing</option>
                    <option>Blog</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="mt-8 flex items-center justify-between">
          <p className="text-xs text-gray-600">MAKE CHANGES AND SAVE</p>
          <div className="flex gap-4">
            <button
              onClick={onCancel}
              className="px-6 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-6 py-2 bg-teal-600 text-white font-medium hover:bg-teal-700 rounded transition-colors"
            >
              {mode === 'edit' ? 'Update Page' : 'Create Page'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePageForm;
